/**
 * Centralized Stage Registry — single source of truth for all stage display names,
 * action items, badge variants, circle colors, and dashboard messages per role.
 * Based on Julia's "Application Stages" CSV.
 *
 * Replaces: getStageDisplayName(), returnFounderStatusStrings(), returnFounderStatusStringsGC(),
 * sowStageFilterOptions, circleIndicators maps, actionBadgeMap (AdminDashboard/MembersList/etc.)
 *
 * The SoW-stage entries counted by the admin dashboard's R28 buckets source
 * their backendValues from the shared grouping table
 * (@eip/shared/types/sowStageGroups) — the same table the server derives its
 * bucket map from — so the list filters and the stat-card counts cannot drift.
 */
import { SOW_STAGE_ENTRY_BACKEND_VALUES } from "@eip/shared/types/sowStageGroups";

export type StageLevel = 'application' | 'sow' | 'invoice';
export type UserRole = 'admin' | 'startup' | 'serviceProvider' | 'ipExpert' | 'growthCoach';
export type BadgeVariant = "destructive" | "warning" | "default" | "secondary" | "outline" | "success";

export type RoleDisplay = {
  displayName: string;
  actionItem: string;
  circleColor?: string;
  badgeVariant?: BadgeVariant;
  description?: string;
};

export type StageDefinition = {
  id: string;
  level: StageLevel;
  backendValues: string[];
  roles: Partial<Record<UserRole, RoleDisplay>>;
  deprecated?: boolean;
};

// ─── STAGE DEFINITIONS ─────────────────────────────────────────────────────────

const stages: StageDefinition[] = [
  // ── Legacy / Missing Statuses ──────────────────────────────────────────────
  {
    id: "predates-update",
    level: "application",
    backendValues: ["n/a"],
    roles: {
      admin: { displayName: "Predates Update", actionItem: "Action items not tracked on portal" },
      growthCoach: { displayName: "Predates Update", actionItem: "Action items not tracked on portal" },
    },
  },
  {
    id: "application-unsubmitted",
    level: "application",
    backendValues: ["unsubmitted"],
    roles: {
      admin: { displayName: "Application Unsubmitted", actionItem: "No action required" },
      growthCoach: { displayName: "Application Unsubmitted", actionItem: "No action required" },
    },
  },
  // ── Pre-SoW (Application Level) ────────────────────────────────────────────
  {
    id: "application-submitted",
    level: "application",
    backendValues: ["submitted", "pending"],
    roles: {
      admin: { displayName: "Application Submitted", actionItem: "Review Application", circleColor: "blueCircle", badgeVariant: "warning" },
      startup: { displayName: "Application Submitted", actionItem: "No action required" },
      growthCoach: { displayName: "Application Submitted", actionItem: "Review Application", circleColor: "blueCircle", badgeVariant: "warning" },
    },
  },
  {
    id: "dd-in-progress",
    level: "application",
    backendValues: ["ddInProgress"],
    roles: {
      admin: { displayName: "Due Diligence in Progress", actionItem: "Continue Due Diligence", circleColor: "orangeCircle", badgeVariant: "default" },
      startup: { displayName: "Due Diligence in Progress", actionItem: "No action required" },
      growthCoach: { displayName: "Due Diligence in Progress", actionItem: "Continue Due Diligence", circleColor: "orangeCircle", badgeVariant: "default" },
    },
  },
  {
    id: "dd-passed",
    level: "application",
    backendValues: ["screened", "approved"],
    roles: {
      admin: { displayName: "Due Diligence Passed", actionItem: "Proceed with IPA/GC", circleColor: "greenCircle", badgeVariant: "default", description: "This company has been screened and can be invited to meet an IPA or can be sent to a Growth Coach for further review" },
      startup: { displayName: "Due Diligence Passed", actionItem: "No action required" },
      growthCoach: { displayName: "Due Diligence Passed", actionItem: "Proceed with IPA/GC", circleColor: "greenCircle", badgeVariant: "default", description: "This company has been screened and can be invited to meet an IPA or can be sent to a Growth Coach for further review" },
    },
  },
  {
    id: "awaiting-gc-assessment",
    level: "application",
    backendValues: ["growthCoachInvite"],
    roles: {
      admin: { displayName: "Awaiting GC Assessment", actionItem: "No action required", description: "A Growth Coach review has been requested. Currently no Growth Coach has claimed this review." },
      startup: { displayName: "Awaiting GC Assessment", actionItem: "No action required" },
      growthCoach: { displayName: "Awaiting GC assessment", actionItem: "Assign GC", circleColor: "greenCircle", badgeVariant: "default", description: "This startup has been selected for review by a Growth Coach. Please assign yourself as the Growth Coach for the appropriate Company." },
    },
  },
  {
    id: "awaiting-gc-review",
    level: "application",
    backendValues: ["growthCoachInProgress"],
    roles: {
      admin: { displayName: "Awaiting GC Review", actionItem: "No action required", description: "A Growth Coach has begun reviewing this Company." },
      startup: { displayName: "Awaiting GC Review", actionItem: "No action required" },
      growthCoach: { displayName: "Awaiting GC Review", actionItem: "Perform GC Review", circleColor: "pinkCircle", badgeVariant: "warning", description: "You have assigned yourself as the Growth Coach for this Account. Please complete and submit your Assessment." },
    },
  },
  {
    id: "gc-assessment-ready",
    level: "application",
    backendValues: ["growthCoachRecommendation"],
    roles: {
      admin: { displayName: "GC Assessment Ready", actionItem: "Review GC Assessment", circleColor: "magentaCircle", badgeVariant: "warning", description: "The Growth Coach has completed their assessment and has selected the outcome: {gcReview}." },
      startup: { displayName: "GC Assessment Ready", actionItem: "No action required" },
      growthCoach: { displayName: "Assessment complete", actionItem: "No action required", description: "You have completed your assessment for this Company. The Account Owner will review your recommendation." },
    },
  },
  {
    id: "awaiting-ipa-invite",
    level: "application",
    backendValues: ["awaitingIpe"],
    roles: {
      admin: { displayName: "Awaiting IPA Invite", actionItem: "No action required", description: "This company has been approved for an IP Advisor Assessment, and are awaiting an Advisor to send a meeting invite." },
      startup: { displayName: "Awaiting IPA Invite", actionItem: "No action required" },
      ipExpert: { displayName: "New Assessment Request", actionItem: "No action required", circleColor: "orangeCircle" },
      growthCoach: { displayName: "Awaiting IPA Invite", actionItem: "No action required", description: "This company has been sent to the growth coaches but as of yet no invite has been sent" },
    },
  },
  {
    id: "coi-with-ipa",
    level: "application",
    backendValues: ["coiWithIpa"],
    roles: {
      admin: { displayName: "COI with IPA", actionItem: "Reassign IPA", circleColor: "redCircle", badgeVariant: "destructive", description: "The assigned IP Advisor has declared a conflict of interest. Please reassign a new IP Advisor." },
      startup: { displayName: "COI with IPA", actionItem: "No action required" },
      growthCoach: { displayName: "COI with IPA", actionItem: "Reassign IPA", circleColor: "redCircle", badgeVariant: "destructive", description: "The assigned IP Advisor has declared a conflict of interest. Please reassign a new IP Advisor." },
    },
  },
  {
    id: "ipa-invite-sent",
    level: "application",
    backendValues: ["ipExpertInvite"],
    roles: {
      admin: { displayName: "IPA Invite Sent", actionItem: "No action required", description: "This startup is now meeting with the IP Advisor. There are no actions required from you at this time." },
      startup: { displayName: "IPA Invite Sent", actionItem: "Please check your email and schedule IP Assessment meeting" },
      ipExpert: { displayName: "Invite Sent", actionItem: "IP Assessment" },
      growthCoach: { displayName: "IPA Invite Sent", actionItem: "No action required", description: "This startup is now meeting with the IP Advisor. There are no actions required from you at this time." },
    },
  },
  {
    id: "ipa-assessment-underway",
    level: "application",
    backendValues: ["ipExpertInProgress"],
    roles: {
      admin: { displayName: "IPA Assessment Underway", actionItem: "No action required", description: "This startup is now meeting with the IP Advisor. There are no actions required from you at this time." },
      startup: { displayName: "IPA Assessment Underway", actionItem: "No action required" },
      ipExpert: { displayName: "Ongoing Assessment", actionItem: "IP Assessment", circleColor: "purpleCircle" },
      growthCoach: { displayName: "IPA Assessment Underway", actionItem: "No action required", description: "This startup is now meeting with the IP Advisor. There are no actions required from you at this time." },
    },
  },
  {
    id: "due-diligence-requested",
    level: "application",
    backendValues: ["dueDiligence"],
    roles: {
      admin: { displayName: "IPA Assessment Underway", actionItem: "Due Diligence", circleColor: "lilacCircle", badgeVariant: "default", description: "IP Advisor has requested further due diligence. Please see the notes and assign to IPA, GC or decline application once due diligence is completed." },
      startup: { displayName: "IPA Assessment Underway", actionItem: "No action required" },
      growthCoach: { displayName: "IPA Assessment Underway", actionItem: "Due Diligence", circleColor: "lilacCircle", badgeVariant: "default", description: "IP Advisor has requested further due diligence. Please see the notes and assign to IPA, GC or decline application once due diligence is completed." },
    },
  },
  {
    id: "ipa-assessment-ready",
    level: "application",
    backendValues: ["ipExpertRecommendation"],
    roles: {
      admin: { displayName: "IPA Assessment Ready", actionItem: "Review IPA Assessment", circleColor: "lilacCircle", badgeVariant: "warning", description: "The IP Advisor's assessment is ready. Please review it." },
      startup: { displayName: "IPA Assessment Ready", actionItem: "No action required" },
      ipExpert: { displayName: "Assessment complete", actionItem: "No action required", circleColor: "greenCircle" },
      growthCoach: { displayName: "IPA Assessment Ready", actionItem: "Review IPA Assessment", circleColor: "lilacCircle", badgeVariant: "warning", description: "The IP Advisor's assessment is ready. Please review it." },
    },
  },
  // ── Service Provider Selection ────────────────────────────────────────────
  {
    id: "confirm-sp",
    level: "application",
    backendValues: ["selectSpf"],
    roles: {
      admin: { displayName: "Confirm Service Provider", actionItem: "No action required", description: "The startup has been sent a request to confirm their Service Provider." },
      startup: { displayName: "Confirm Service Provider", actionItem: "No action required" },
      growthCoach: { displayName: "Working With Service Provider", actionItem: "Working With Service Provider" },
    },
  },
  {
    id: "new-sp-requested",
    level: "application",
    backendValues: ["newSpfRequested"],
    roles: {
      admin: { displayName: "Confirm Service Provider", actionItem: "Confirm New Provider", circleColor: "magentaCircle", badgeVariant: "warning", description: "The startup has sent a request to confirm a new Service Provider." },
      startup: { displayName: "Confirm Service Provider", actionItem: "No action required" },
      growthCoach: { displayName: "Working With Service Provider", actionItem: "Working With Service Provider" },
    },
  },
  // ── SoW Lifecycle (Per-SoW Level) ─────────────────────────────────────────
  {
    id: "sow-requested",
    level: "sow",
    backendValues: [...SOW_STAGE_ENTRY_BACKEND_VALUES.sowRequested],
    roles: {
      admin: { displayName: "SoW Requested", actionItem: "No action required", description: "The startup and service provider have been notified to create a statement of work." },
      startup: { displayName: "SoW Requested", actionItem: "No action required" },
      serviceProvider: { displayName: "New SoW Request", actionItem: "Prepare SoW", circleColor: "redCircle", badgeVariant: "warning" },
      ipExpert: { displayName: "Assessment complete", actionItem: "No action required" },
      growthCoach: { displayName: "Working With Service Provider", actionItem: "Working With Service Provider" },
    },
  },
  {
    id: "sow-prep-in-progress",
    level: "sow",
    backendValues: ["sowInProgress", "Unsubmitted SoW", "SoW In Progress"],
    roles: {
      admin: { displayName: "SoW Preparation in Progress", actionItem: "No action required", description: "The Service Provider has started drafting the Statement of Work." },
      startup: { displayName: "SoW Preparation in Progress", actionItem: "No action required" },
      serviceProvider: { displayName: "SoW In Progress", actionItem: "Prepare SoW", circleColor: "orangeCircle", badgeVariant: "default" },
      ipExpert: { displayName: "Assessment complete", actionItem: "No action required" },
      growthCoach: { displayName: "Working With Service Provider", actionItem: "Working With Service Provider" },
    },
  },
  {
    id: "sow-awaiting-ipa-review",
    level: "sow",
    backendValues: [...SOW_STAGE_ENTRY_BACKEND_VALUES.sowAwaitingIpaReview],
    roles: {
      admin: { displayName: "SoW Awaiting IPA review", actionItem: "No action required", circleColor: "yellowCircle", description: "The Service Provider has submitted the SoW. Please review it." },
      startup: { displayName: "SoW Awaiting IPA review", actionItem: "No action required" },
      serviceProvider: { displayName: "SoW Under Review", actionItem: "No action required", badgeVariant: "secondary" },
      ipExpert: { displayName: "SoW Under Review", actionItem: "Review SoW", circleColor: "blueCircle" },
      growthCoach: { displayName: "Working With Service Provider", actionItem: "Working With Service Provider" },
    },
  },
  {
    id: "sow-awaiting-ao-review",
    level: "sow",
    backendValues: [...SOW_STAGE_ENTRY_BACKEND_VALUES.sowAwaitingAoReview],
    roles: {
      admin: { displayName: "SoW Awaiting AO Review", actionItem: "Review SoW", circleColor: "yellowCircle", badgeVariant: "warning", description: "The IP Advisor has reviewed this SoW. Please review it." },
      startup: { displayName: "SoW Awaiting AO Review", actionItem: "No action required" },
      serviceProvider: { displayName: "SoW Under Review", actionItem: "No action required", badgeVariant: "secondary" },
      ipExpert: { displayName: "SoW review complete", actionItem: "No action required" },
      growthCoach: { displayName: "Working With Service Provider", actionItem: "Working With Service Provider" },
    },
  },
  {
    id: "sow-edits-requested-from-sp",
    level: "sow",
    backendValues: ["sowEditRequested", "Edit Requested from Admin"],
    roles: {
      admin: { displayName: "SoW Edits Requested from SP", actionItem: "No action required", description: "An Admin has requested edits to the SoW provided by the Service Provider. The SP has been notified." },
      startup: { displayName: "SoW Edits Requested from SP", actionItem: "No action required" },
      serviceProvider: { displayName: "SoW Edits Requested", actionItem: "Revisit SoW", circleColor: "lilacCircle", badgeVariant: "warning" },
      ipExpert: { displayName: "SoW review complete", actionItem: "No action required" },
      growthCoach: { displayName: "Working With Service Provider", actionItem: "Working With Service Provider" },
    },
  },
  {
    id: "sow-ready-for-audit",
    level: "sow",
    backendValues: [...SOW_STAGE_ENTRY_BACKEND_VALUES.sowReadyForAudit],
    roles: {
      admin: { displayName: "SoW Ready for Audit", actionItem: "No action required", circleColor: "orangeCircle", description: "The IP Advisor has approved this SoW. Please review it." },
      startup: { displayName: "SoW Ready for Audit", actionItem: "No action required" },
      serviceProvider: { displayName: "SoW Under Review", actionItem: "No action required", badgeVariant: "secondary" },
      ipExpert: { displayName: "SoW review complete", actionItem: "No action required" },
      growthCoach: { displayName: "SoW Ready for Audit", actionItem: "Perform Audit", circleColor: "orangeCircle", badgeVariant: "default", description: "The SoW has been approved by the IP Advisor and Admin. Please perform the audit." },
    },
  },
  // Audit sub-states (internal admin-only, not in Julia's spreadsheet)
  {
    id: "audit-clarification-needed",
    level: "sow",
    backendValues: [...SOW_STAGE_ENTRY_BACKEND_VALUES.auditClarificationNeeded],
    roles: {
      admin: { displayName: "SoW Awaiting IPA review", actionItem: "No action required", description: "Audit clarification has been requested." },
    },
  },
  {
    id: "audit-update-needed",
    level: "sow",
    backendValues: [...SOW_STAGE_ENTRY_BACKEND_VALUES.auditUpdateNeeded],
    roles: {
      admin: { displayName: "SoW Awaiting IPA review", actionItem: "No action required", description: "Audit update has been requested." },
    },
  },
  {
    id: "sow-ready-for-signature",
    level: "sow",
    backendValues: [...SOW_STAGE_ENTRY_BACKEND_VALUES.sowReadyForSignature],
    roles: {
      admin: { displayName: "SoW Ready for Signature", actionItem: "Send for signatures", circleColor: "orangeCircle", badgeVariant: "default", description: "Please send the SoW for e-signatures so work may proceed." },
      startup: { displayName: "SoW Ready for Signature", actionItem: "No action required" },
      serviceProvider: { displayName: "SoW Ready for Signature", actionItem: "No action required", badgeVariant: "secondary" },
      ipExpert: { displayName: "SoW review complete", actionItem: "No action required" },
      growthCoach: { displayName: "Working With Service Provider", actionItem: "Working With Service Provider" },
    },
  },
  {
    id: "sow-awaiting-signatures",
    level: "sow",
    backendValues: ["Awaiting Signatures"],
    roles: {
      admin: { displayName: "Awaiting E-Signatures", actionItem: "No action required", description: "SoW has been sent for e-signatures. Waiting for founder and service provider to sign." },
      startup: { displayName: "SoW Awaiting Your Signature", actionItem: "Sign the SoW", circleColor: "orangeCircle" },
      serviceProvider: { displayName: "SoW Awaiting Your Signature", actionItem: "Sign the SoW", circleColor: "orangeCircle" },
      ipExpert: { displayName: "SoW review complete", actionItem: "No action required" },
      growthCoach: { displayName: "Working With Service Provider", actionItem: "Working With Service Provider" },
    },
  },
  {
    id: "sow-cancelled",
    level: "sow",
    backendValues: ["Cancelled"],
    roles: {
      admin: { displayName: "SoW Cancelled", actionItem: "No action required", badgeVariant: "destructive" },
      startup: { displayName: "SoW Cancelled", actionItem: "No action required" },
      serviceProvider: { displayName: "SoW Cancelled", actionItem: "No action required", badgeVariant: "destructive" },
      ipExpert: { displayName: "SoW Cancelled", actionItem: "No action required" },
      growthCoach: { displayName: "SoW Cancelled", actionItem: "No action required" },
    },
  },
  {
    id: "sow-signed",
    level: "sow",
    backendValues: [...SOW_STAGE_ENTRY_BACKEND_VALUES.sowSigned],
    roles: {
      admin: { displayName: "SoW Signed", actionItem: "No action required" },
      startup: { displayName: "SoW Signed", actionItem: "No action required" },
      serviceProvider: { displayName: "SoW Signed", actionItem: "Deliver services", badgeVariant: "success" },
      ipExpert: { displayName: "SoW Signed", actionItem: "No action required" },
      growthCoach: { displayName: "Working With Service Provider", actionItem: "Working With Service Provider" },
    },
  },
  {
    id: "work-in-progress",
    level: "sow",
    backendValues: ["Work In Progress"],
    roles: {
      admin: { displayName: "Work In Progress", actionItem: "No action required" },
      startup: { displayName: "SoW Signed", actionItem: "No action required" },
      serviceProvider: { displayName: "SoW Signed", actionItem: "Deliver services", badgeVariant: "default" },
      growthCoach: { displayName: "Working With Service Provider", actionItem: "Working With Service Provider" },
    },
  },
  {
    id: "ready-for-invoice",
    level: "sow",
    backendValues: ["Ready for Invoice"],
    roles: {
      admin: { displayName: "Ready for Invoice", actionItem: "No action required" },
      startup: { displayName: "SoW Signed", actionItem: "No action required" },
      serviceProvider: { displayName: "SoW Signed", actionItem: "Deliver services", badgeVariant: "success" },
      growthCoach: { displayName: "Working With Service Provider", actionItem: "Working With Service Provider" },
    },
  },
  {
    id: "invoice-in-progress",
    level: "sow",
    backendValues: ["Invoice in Progress"],
    roles: {
      admin: { displayName: "Invoice in Progress", actionItem: "No action required" },
      startup: { displayName: "SoW Signed", actionItem: "No action required" },
      serviceProvider: { displayName: "Final invoice submitted", actionItem: "No action required" },
      growthCoach: { displayName: "Working With Service Provider", actionItem: "Working With Service Provider" },
    },
  },
  // ── Retainer in Progress (ApplicationStatus) ─────────────────────────────
  {
    id: "retainer-in-progress",
    level: "sow",
    backendValues: ["retainerInProgress"],
    roles: {
      admin: { displayName: "Retainer in Progress", actionItem: "No action required" },
      startup: { displayName: "SoW Signed", actionItem: "No action required" },
      serviceProvider: { displayName: "Retainer submitted", actionItem: "Deliver services", badgeVariant: "default" },
      growthCoach: { displayName: "Working With Service Provider", actionItem: "Working With Service Provider" },
    },
  },
  // ── Invoice Lifecycle (Per-Invoice Level) ────────────────────────────────
  // Note: Invoice statuses are the same for retainer and final invoices.
  // The registry stores generic entries keyed by backend value. For
  // retainer/final-specific display names, use getInvoiceDisplayName().
  {
    id: "invoice-in-progress-creation",
    level: "invoice",
    backendValues: ["inprogress"],
    roles: {
      admin: { displayName: "Invoice Creation in Progress", actionItem: "No action required" },
    },
  },
  {
    id: "invoice-submitted",
    level: "invoice",
    backendValues: ["submitted"],
    roles: {
      admin: { displayName: "Invoice Submitted", actionItem: "Review Invoice", badgeVariant: "warning" },
      serviceProvider: { displayName: "Invoice Submitted", actionItem: "No action required", badgeVariant: "secondary" },
      startup: { displayName: "SoW Signed", actionItem: "No action required" },
      growthCoach: { displayName: "SoW Signed", actionItem: "No action required" },
    },
  },
  {
    id: "invoice-revisions-requested",
    level: "invoice",
    backendValues: ["revisionsRequested"],
    roles: {
      admin: { displayName: "Invoice revision requested", actionItem: "No action required" },
      serviceProvider: { displayName: "Invoice revision requested", actionItem: "Revisit invoice", badgeVariant: "warning" },
      startup: { displayName: "SoW Signed", actionItem: "No action required" },
      growthCoach: { displayName: "SoW Signed", actionItem: "No action required" },
    },
  },
  {
    id: "invoice-approved",
    level: "invoice",
    backendValues: ["invoiceApproved"],
    roles: {
      admin: { displayName: "Invoice Approved", actionItem: "Mark Sent to Finance" },
      serviceProvider: { displayName: "Invoice Submitted", actionItem: "No action required", badgeVariant: "secondary" },
      startup: { displayName: "SoW Signed", actionItem: "No action required" },
      growthCoach: { displayName: "SoW Signed", actionItem: "No action required" },
    },
  },
  {
    id: "invoice-sent-to-finance",
    level: "invoice",
    backendValues: ["sentToFinance"],
    roles: {
      admin: { displayName: "Sent to Finance", actionItem: "No action required" },
      serviceProvider: { displayName: "Sent to Finance", actionItem: "No action required" },
      startup: { displayName: "SoW Signed", actionItem: "No action required" },
      growthCoach: { displayName: "SoW Signed", actionItem: "No action required" },
    },
  },
  // ── Invoicing Complete (all SoWs closed, awaiting survey) ────────────────
  {
    id: "invoicing-complete",
    level: "application",
    backendValues: ["sowComplete"],
    roles: {
      admin: { displayName: "Invoicing Complete", actionItem: "Send survey", badgeVariant: "default" },
      startup: { displayName: "Project completed", actionItem: "No action required" },
      serviceProvider: { displayName: "Project Completed", actionItem: "No action required", badgeVariant: "outline" },
      growthCoach: { displayName: "Invoicing Complete", actionItem: "No action required" },
    },
  },
  // ── Survey Phase ─────────────────────────────────────────────────────────
  {
    id: "survey-sent",
    level: "application",
    backendValues: ["surveySent"],
    roles: {
      admin: { displayName: "Survey Sent", actionItem: "Await survey response", badgeVariant: "default" },
      startup: { displayName: "Survey Sent", actionItem: "Complete survey" },
      serviceProvider: { displayName: "Project Completed", actionItem: "No action required", badgeVariant: "outline" },
      growthCoach: { displayName: "Survey Sent", actionItem: "No action required" },
    },
  },
  {
    id: "survey-response-received",
    level: "application",
    backendValues: ["surveyComplete"],
    roles: {
      admin: { displayName: "Survey Response Received", actionItem: "Mark project completed", badgeVariant: "default" },
      startup: { displayName: "Survey Response Received", actionItem: "No action required" },
      serviceProvider: { displayName: "Project Completed", actionItem: "No action required", badgeVariant: "outline" },
      growthCoach: { displayName: "Survey Response Received", actionItem: "No action required" },
    },
  },
  // ── Project Completion ────────────────────────────────────────────────────
  {
    id: "project-completed",
    level: "application",
    backendValues: ["projectCompleted", "partnerOrgProjectCompleted", "Project Closed"],
    roles: {
      admin: { displayName: "Project Completed", actionItem: "No action required" },
      startup: { displayName: "Project completed", actionItem: "No action required" },
      serviceProvider: { displayName: "Project Completed", actionItem: "No action required", badgeVariant: "outline" },
    },
  },
  {
    id: "application-closed",
    level: "application",
    backendValues: ["closed"],
    roles: {
      admin: { displayName: "Application Closed", actionItem: "No action required" },
      startup: { displayName: "Application Closed", actionItem: "No action required" },
      growthCoach: { displayName: "Application Closed", actionItem: "No action required" },
    },
  },
  // ── Rejection ─────────────────────────────────────────────────────────────
  {
    id: "rejection-flagged",
    level: "application",
    backendValues: ["rejectedFlagged"],
    roles: {
      admin: { displayName: "Rejection Flagged", actionItem: "Add Rejection Reason", circleColor: "redCircle", badgeVariant: "destructive", description: "This company has been flagged for rejection, but no reasons are yet provided and email has not been sent" },
      startup: { displayName: "Rejection Flagged", actionItem: "No action required" },
      growthCoach: { displayName: "Rejection Flagged", actionItem: "Add Rejection Reason", circleColor: "redCircle", badgeVariant: "destructive" },
    },
  },
  {
    id: "rejection-in-progress",
    level: "application",
    backendValues: ["rejectedInProgress"],
    roles: {
      admin: { displayName: "Rejection Flagged", actionItem: "Send Rejection Email", circleColor: "redCircle", badgeVariant: "destructive", description: "This company has been flagged for rejection, reasons are either incomplete or no email has been sent" },
      startup: { displayName: "Rejection Flagged", actionItem: "No action required" },
      growthCoach: { displayName: "Rejection Flagged", actionItem: "Send Rejection Email", circleColor: "redCircle", badgeVariant: "destructive", description: "This company has been flagged for rejection, reasons are either incomplete or no email has been sent" },
    },
  },
  {
    id: "application-rejected",
    level: "application",
    backendValues: ["rejected", "rejectedReapply"],
    roles: {
      admin: { displayName: "Application Rejected", actionItem: "No action required" },
      startup: { displayName: "Application Rejected", actionItem: "No action required" },
      ipExpert: { displayName: "Application Rejected", actionItem: "No action required" },
      growthCoach: { displayName: "Application Rejected", actionItem: "No action required" },
    },
  },
  // ── Edit Request ──────────────────────────────────────────────────────────
  {
    id: "pending-edit-request",
    level: "application",
    backendValues: ["pendingEditRequest"],
    roles: {
      admin: { displayName: "Application Edit Requested", actionItem: "No action required", description: "This application is open for modification by the company. Once it is updated, the application will return to it's most recent status." },
      startup: { displayName: "Application Edit Requested", actionItem: "Edit Application" },
      growthCoach: { displayName: "Application Edit Requested", actionItem: "No action required", description: "This application is open for modification by the company. Once it is updated, the application will return to it's most recent status." },
    },
  },
  // ── IPA Board Review ──────────────────────────────────────────────────────
  {
    id: "ipa-board-review",
    level: "application",
    backendValues: ["ipeBoardReview"],
    roles: {
      admin: { displayName: "IPA Board Review", actionItem: "No action required", description: "The IP Advisor has flagged this application for review with the Board." },
      growthCoach: { displayName: "IPA Board Review", actionItem: "No action required", description: "The IP Advisor has flagged this application for review with the Board. There are no actions required from you at this time." },
    },
  },
  // ── SoW Approval (legacy ApplicationStatus) ──────────────────────────────
  {
    id: "sow-approval",
    level: "application",
    backendValues: ["sowApproved"],
    roles: {
      admin: { displayName: "SoW Approval", actionItem: "No action required" },
      growthCoach: { displayName: "Working With Service Provider", actionItem: "Working With Service Provider" },
    },
  },
  // ── Deprecated fallbacks (backward compat for legacy DB docs) ─────────────
  {
    id: "deprecated-awaiting-startup-approval",
    level: "sow",
    backendValues: ["Awaiting Startup Approval"],
    deprecated: true,
    roles: {
      admin: { displayName: "SoW Preparation in Progress", actionItem: "No action required" },
    },
  },
  {
    id: "deprecated-edit-request-from-startup",
    level: "sow",
    backendValues: ["Edit Request from Startup"],
    deprecated: true,
    roles: {
      admin: { displayName: "SoW Preparation in Progress", actionItem: "No action required" },
    },
  },
  {
    id: "deprecated-ready-for-audit-2",
    level: "sow",
    backendValues: [...SOW_STAGE_ENTRY_BACKEND_VALUES.deprecatedReadyForAudit2],
    deprecated: true,
    roles: {
      admin: { displayName: "SoW Ready for Audit", actionItem: "No action required" },
    },
  },
  {
    id: "deprecated-clarification-needed-2",
    level: "sow",
    backendValues: [...SOW_STAGE_ENTRY_BACKEND_VALUES.deprecatedClarificationNeeded2],
    deprecated: true,
    roles: {
      admin: { displayName: "SoW Awaiting IPA review", actionItem: "No action required" },
    },
  },
  {
    id: "deprecated-update-needed-2",
    level: "sow",
    backendValues: [...SOW_STAGE_ENTRY_BACKEND_VALUES.deprecatedUpdateNeeded2],
    deprecated: true,
    roles: {
      admin: { displayName: "SoW Awaiting IPA review", actionItem: "No action required" },
    },
  },
];

// ─── LOOKUP INDEX ───────────────────────────────────────────────────────────────

const lookupMap = new Map<string, StageDefinition>();
for (const stage of stages) {
  for (const val of stage.backendValues) {
    lookupMap.set(val, stage);
  }
}

function findStage(backendValue: string): StageDefinition | undefined {
  return lookupMap.get(backendValue);
}

// ─── PUBLIC API ─────────────────────────────────────────────────────────────────

export function getDisplayName(backendValue: string | undefined, role: UserRole = 'admin'): string {
  if (!backendValue) return "Other";
  const stage = findStage(backendValue);
  if (!stage) return "Other";
  return stage.roles[role]?.displayName ?? stage.roles.admin?.displayName ?? "Other";
}

export function getActionItem(backendValue: string | undefined, role: UserRole = 'admin'): string {
  if (!backendValue) return "No action required";
  const stage = findStage(backendValue);
  if (!stage) return "No action required";
  return stage.roles[role]?.actionItem ?? "No action required";
}

export function getCircleColor(backendValue: string | undefined, role: UserRole = 'admin'): string {
  if (!backendValue) return "";
  const stage = findStage(backendValue);
  if (!stage) return "";
  return stage.roles[role]?.circleColor ?? "";
}

export function getBadgeVariant(backendValue: string | undefined, role: UserRole = 'admin'): BadgeVariant {
  if (!backendValue) return "outline";
  const stage = findStage(backendValue);
  if (!stage) return "outline";
  return stage.roles[role]?.badgeVariant ?? "outline";
}

export function getDescription(backendValue: string | undefined, role: UserRole = 'admin', context?: Record<string, string>): string {
  if (!backendValue) return "";
  const stage = findStage(backendValue);
  if (!stage) return "";
  let desc = stage.roles[role]?.description ?? "";
  if (context) {
    for (const [key, val] of Object.entries(context)) {
      desc = desc.replace(`{${key}}`, val);
    }
  }
  return desc;
}

export function getStageId(backendValue: string | undefined): string | undefined {
  if (!backendValue) return undefined;
  return findStage(backendValue)?.id;
}

/** Look up badge variant by action item text (for consumers receiving pre-formatted strings) */
export function getBadgeVariantByActionItem(actionItem: string, role: UserRole = 'admin'): BadgeVariant {
  for (const stage of stages) {
    if (stage.roles[role]?.actionItem === actionItem) {
      return stage.roles[role]?.badgeVariant ?? "outline";
    }
  }
  return "outline";
}

/** Look up circle color by action item text (for List.tsx circle indicators) */
export function getCircleColorByActionItem(actionItem: string, role: UserRole = 'admin'): string {
  for (const stage of stages) {
    if (stage.roles[role]?.actionItem === actionItem) {
      return stage.roles[role]?.circleColor ?? "";
    }
  }
  return "";
}

/** Get all distinct actionable action items for a role (for sort/filter dropdowns) */
export function getDistinctActionItems(role: UserRole = 'admin'): string[] {
  const items = new Set<string>();
  for (const stage of stages) {
    if (stage.deprecated) continue;
    const ai = stage.roles[role]?.actionItem;
    if (ai && ai !== 'No action required') items.add(ai);
  }
  return Array.from(items);
}

/** Returns filter options for SoW stage dropdowns */
export function getSowFilterOptions(role: UserRole = 'admin'): string[] {
  const names = new Set<string>();
  for (const stage of stages) {
    if (stage.level === 'sow' && !stage.deprecated) {
      const display = stage.roles[role]?.displayName ?? stage.roles.admin?.displayName;
      if (display) names.add(display);
    }
  }
  return ["All", ...Array.from(names)];
}

/** Returns [displayName, circleColor, actionItem, description] for backward compat */
export function getStatusArray(backendValue: string | undefined, role: UserRole = 'admin', context?: Record<string, string>): string[] {
  return [
    getDisplayName(backendValue, role),
    getCircleColor(backendValue, role),
    getActionItem(backendValue, role),
    getDescription(backendValue, role, context),
  ];
}

/** Returns invoice display name with retainer/final distinction per Julia's CSV */
export function getInvoiceDisplayName(backendValue: string | undefined, isRetainer: boolean, role: UserRole = 'admin'): string {
  if (!backendValue) return "Other";
  if (isRetainer) {
    switch (backendValue) {
      case "submitted": return "Retainer submitted";
      case "invoiceApproved": return "Retainer approved";
      case "sentToFinance": return "Retainer sent to Finance";
      case "revisionsRequested": return "Invoice revision requested";
      default: return getDisplayName(backendValue, role);
    }
  }
  switch (backendValue) {
    case "submitted": return "Final invoice submitted";
    case "invoiceApproved": return "Final invoice approved";
    case "sentToFinance": return "Invoice sent to Finance";
    case "revisionsRequested": return "Invoice revision requested";
    default: return getDisplayName(backendValue, role);
  }
}

/** All stage definitions (for iteration) */
export const allStages = stages;

// ─── STATUS CONSTANT GROUPS (for conditional logic) ─────────────────────────

export const APP_TERMINAL_STATUSES = ['closed', 'projectCompleted', 'partnerOrgProjectCompleted'] as const;
export const SOW_WORK_STARTED_STATUSES = ['Work In Progress', 'Ready for Invoice', 'Invoice in Progress', 'Project Closed'] as const;
export const APP_REJECTION_STATUSES = ['rejected', 'rejectedFlagged', 'rejectedInProgress', 'rejectedReapply'] as const;

// ─── PHASE GROUPS (for filter UI) ────────────────────────────────────────────

export type PhaseGroup = {
  id: string;
  label: string;
  stageIds: string[];
};

// Filter dropdown structure. Each stageId is the canonical stage for its admin
// display name — stages that share the same display name (e.g. "IPA Assessment
// Underway" is used by both `ipa-assessment-underway` and `due-diligence-requested`)
// are deduped here. Filtering by a canonical stage automatically expands to all
// backend values that share its display name (see resolveFilterToBackendValues).
export const PHASE_GROUPS: PhaseGroup[] = [
  { id: "review", label: "Application Review", stageIds: ["application-submitted", "dd-in-progress", "dd-passed"] },
  { id: "assessment", label: "Assessment", stageIds: ["awaiting-gc-assessment", "awaiting-gc-review", "gc-assessment-ready", "awaiting-ipa-invite", "coi-with-ipa", "ipa-invite-sent", "ipa-assessment-underway", "ipa-assessment-ready"] },
  { id: "sow", label: "SoW Phase", stageIds: ["sow-requested", "sow-prep-in-progress", "sow-awaiting-ipa-review", "sow-awaiting-ao-review", "sow-edits-requested-from-sp", "sow-ready-for-audit", "sow-ready-for-signature", "sow-cancelled", "sow-signed", "invoice-in-progress"] },
  { id: "completed", label: "Completed / Closed", stageIds: ["invoicing-complete", "survey-sent", "survey-response-received", "project-completed", "application-closed"] },
  { id: "rejected", label: "Rejected", stageIds: ["rejection-flagged", "application-rejected"] },
  { id: "edit-request", label: "Edit Request", stageIds: ["pending-edit-request"] },
  { id: "other", label: "Other", stageIds: ["predates-update", "application-unsubmitted"] },
];

/** Resolve a phase or stage filter value to a set of backend status values.
 *  When filtering by a single stage, automatically includes backend values from
 *  any other stages sharing the same admin display name, so visually-deduped
 *  dropdown entries still match all underlying records. */
export function resolveFilterToBackendValues(filterValue: string): string[] | null {
  if (filterValue === "All") return null;
  if (filterValue.startsWith("phase:")) {
    const phaseId = filterValue.slice(6);
    const phase = PHASE_GROUPS.find(p => p.id === phaseId);
    if (!phase) return null;
    return phase.stageIds.flatMap(id => expandStageBackendValues(id));
  }
  return expandStageBackendValues(filterValue);
}

function expandStageBackendValues(stageId: string): string[] {
  const stage = stages.find(s => s.id === stageId);
  if (!stage) return [];
  const targetDisplayName = stage.roles.admin?.displayName;
  if (!targetDisplayName) return stage.backendValues;
  const sameNameStages = stages.filter(
    s => !s.deprecated && s.roles.admin?.displayName === targetDisplayName
  );
  return sameNameStages.flatMap(s => s.backendValues);
}

/** Check if a filter value is in the SoW phase group */
export function isInSowPhase(filterValue: string): boolean {
  if (filterValue === "phase:sow") return true;
  const sowPhase = PHASE_GROUPS.find(p => p.id === "sow");
  return sowPhase?.stageIds.includes(filterValue) ?? false;
}

/** Get admin display name for a stage ID (for filter dropdown labels) */
export function getDisplayNameForStageId(stageId: string): string {
  const stage = stages.find(s => s.id === stageId);
  return stage?.roles.admin?.displayName ?? "Other";
}

/** Get the display label for a filter value (phase group or stage ID) */
export function getFilterDisplayLabel(filterValue: string): string {
  if (filterValue === "All") return "All";
  if (filterValue.startsWith("phase:")) {
    const phaseId = filterValue.slice(6);
    const phase = PHASE_GROUPS.find(p => p.id === phaseId);
    return phase ? `All ${phase.label}` : "Other";
  }
  return getDisplayNameForStageId(filterValue);
}
